<template>
    <div class="comment-login-area">
        <Login />
        <div class="content comments-section">
            <h3>Comments</h3>

            <!-- Comment Form -->
            <form v-if="user" @submit.prevent="submitComment">

                <div class="row mb-3">
                    <div class="col-12">
                        <div class="user-info">
                            <div class="avatar">
                                <img v-if="user.profilePicture" :src="user.profilePicture" alt="User Profile" class="profile-pic" />
                                <span v-else class="avatar-placeholder">{{ getInitials(user.user) }}</span>
                            </div>
                            <p><strong>Posting as: {{ user.user }}</strong></p>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-12">
                        <textarea v-model="content" class="form-control"  placeholder="Write a comment..." required></textarea>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12">
                        <button type="submit" class="btn btn-success">Post Comment</button>
                    </div>
                </div>
            </form>

            <p v-else class="login-prompt">Please log in to post a comment.</p>

            <!-- Comment List -->
            <ul v-if="comments.length > 0" class="comment-list">
                <li v-for="comment in comments" :key="comment.id" class="comment-item">
                    <div class="avatar">
                        <img v-if="comment.profilePicture" :src="comment.profilePicture" alt="User Profile" class="profile-pic" />
                        <span v-else class="avatar-placeholder">{{ getInitials(comment.author) }}</span>
                    </div>
                    <div class="comment-bubble">
                        <div class="comment-header">
                            <strong class="author">{{ comment.author }}</strong>
                            <small class="comment-time">{{ formatDate(comment.createdAt) }}</small>
                        </div>
                        <p class="comment-text">{{ comment.content }}</p>
                    </div>
                </li>
            </ul>

            <p v-else class="no-comments">No comments yet. Be the first to comment!</p>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia';
    import Login from "./Login.vue";
    import { fetchWithAuth } from "../../utils/authFetch";
    import { useAuthStore } from "../../utils/authStore";

    export default {
        components: {
            Login
        },
        data() {
            return {
                comments: [],
                content: ""
            };
        },
        computed: {
            ...mapState(useAuthStore, ['user']),
            isAdmin() {
                return this.user?.roles?.includes("Admin");
            },
            currentRoutePath() {
                return this.$route.path.replace(/^\/+/, ''); // Removes the first `/`
            }
        },
        watch: {
            $route(to) {
                this.fetchComments();
            }
        },
        methods: {
            async fetchComments() {
                try {
                    const response = await fetch(`/comments/${this.currentRoutePath}`);
                    this.comments = await response.json();
                } catch (error) {
                    console.error("Error loading comments:", error);
                }
            },
            async submitComment() {
                if (!this.user || !this.content) return;

                const newComment = {
                    pageUrl: this.currentRoutePath,
                    author: this.user.user,
                    profilePicture: this.user.profilePicture || "", // Keep empty string if no picture
                    content: this.content
                };

                try {
                    await fetchWithAuth("/comments", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(newComment)
                    });

                    this.content = "";
                    this.fetchComments();
                } catch (error) {
                    console.error("Error submitting comment:", error);
                }
            },
            formatDate(dateString) {
                return new Date(dateString).toLocaleString();
            },
            getInitials(name) {
                if (!name) return "?";
                return name
                    .split(" ")
                    .map(part => part[0]?.toUpperCase() || "")
                    .join("")
                    .substring(0, 2);
            }
        },
        async mounted() {
            this.fetchComments();
        }
    };
</script>

<style scoped>
    /* Layout */
    .comment-login-area {
        display: flex;
    }

    /* Comments Section */
    .comments-section {
        padding: 20px;
        border-radius: 8px;
        margin-top: 20px;
        margin-left: 20px;
        width: 100%;
    }

    /* Form */
    form {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
    }

    .user-info {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #62007f;
        font-weight: bold;
        font-size: 14px;
        min-width: 40px;
    }

    .profile-pic {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .avatar-placeholder {
        font-size: 16px;
    }

    /* Comments List */
    .comment-list {
        list-style: none;
        padding: 0;
    }

    .comment-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 15px;
    }

    /* Comment Bubble */
    .comment-bubble {
        background: #4b215d;
        color: white;
        padding: 12px;
        border-radius: 10px;
        position: relative;
        max-width: 80%;
        box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.1);
    }

        /* Speech Bubble Effect */
        .comment-bubble::before {
            content: "";
            position: absolute;
            top: 10px;
            left: -14px;
            border-width: 8px;
            border-style: solid;
            border-color: transparent #4b215d transparent transparent;
        }

    .comment-text {
        display: table;
        table-layout: fixed;
        word-wrap: break-word;
        width: 100%;
    }
    /* Comment Header */
    .comment-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    strong.author {
        margin-right: 20px;
    }

    .author {
        font-weight: bold;
    }

    .comment-time {
        font-size: 12px;
    }

    /* Responsive */
    @media (max-width: 1024px) {
        .comment-login-area {
            display: block;
        }

        .comments-section {
            margin-left: 0;
            width: inherit;
        }
    }
</style>
